/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";

// Material Dashboard 2 React base styles
import colors from "materialDashboard/theme/base/colors";
import breakpoints from "materialDashboard/theme/base/breakpoints";
import typography from "materialDashboard/theme/base/typography";
import boxShadows from "materialDashboard/theme/base/boxShadows";
import borders from "materialDashboard/theme/base/borders";
import globals from "materialDashboard/theme/base/globals";

// Material Dashboard 2 React helper functions
import boxShadow from "materialDashboard/theme/functions/boxShadow";
import hexToRgb from "materialDashboard/theme/functions/hexToRgb";
import linearGradient from "materialDashboard/theme/functions/linearGradient";
import pxToRem from "materialDashboard/theme/functions/pxToRem";
import rgba from "materialDashboard/theme/functions/rgba";

// Material Dashboard 2 React components base styles for @mui material components
import sidenav from "materialDashboard/theme/components/sidenav";
import list from "materialDashboard/theme/components/list";
import listItem from "materialDashboard/theme/components/list/listItem";
import listItemText from "materialDashboard/theme/components/list/listItemText";
import card from "materialDashboard/theme/components/card";
import cardMedia from "materialDashboard/theme/components/card/cardMedia";
import cardContent from "materialDashboard/theme/components/card/cardContent";
import button from "materialDashboard/theme/components/button";
import iconButton from "materialDashboard/theme/components/iconButton";
import input from "materialDashboard/theme/components/form/input";
import inputLabel from "materialDashboard/theme/components/form/inputLabel";
import inputOutlined from "materialDashboard/theme/components/form/inputOutlined";
import textField from "materialDashboard/theme/components/form/textField";
import menu from "materialDashboard/theme/components/menu";
import menuItem from "materialDashboard/theme/components/menu/menuItem";
import switchButton from "materialDashboard/theme/components/form/switchButton";
import divider from "materialDashboard/theme/components/divider";
import tableContainer from "materialDashboard/theme/components/table/tableContainer";
import tableHead from "materialDashboard/theme/components/table/tableHead";
import tableCell from "materialDashboard/theme/components/table/tableCell";
import linearProgress from "materialDashboard/theme/components/linearProgress";
import breadcrumbs from "materialDashboard/theme/components/breadcrumbs";
import slider from "materialDashboard/theme/components/slider";
import avatar from "materialDashboard/theme/components/avatar";
import tooltip from "materialDashboard/theme/components/tooltip";
import appBar from "materialDashboard/theme/components/appBar";
import tabs from "materialDashboard/theme/components/tabs";
import tab from "materialDashboard/theme/components/tabs/tab";
import stepper from "materialDashboard/theme/components/stepper";
import step from "materialDashboard/theme/components/stepper/step";
import stepConnector from "materialDashboard/theme/components/stepper/stepConnector";
import stepLabel from "materialDashboard/theme/components/stepper/stepLabel";
import stepIcon from "materialDashboard/theme/components/stepper/stepIcon";
import select from "materialDashboard/theme/components/form/select";
import formControlLabel from "materialDashboard/theme/components/form/formControlLabel";
import formLabel from "materialDashboard/theme/components/form/formLabel";
import radio from "materialDashboard/theme/components/form/radio";
import autocomplete from "materialDashboard/theme/components/form/autocomplete";
import container from "materialDashboard/theme/components/container";
import popover from "materialDashboard/theme/components/popover";
import buttonBase from "materialDashboard/theme/components/buttonBase";
import icon from "materialDashboard/theme/components/icon";
import svgIcon from "materialDashboard/theme/components/svgIcon";
import link from "materialDashboard/theme/components/link";
import dialog from "materialDashboard/theme/components/dialog";
import dialogTitle from "materialDashboard/theme/components/dialog/dialogTitle";
import dialogContent from "materialDashboard/theme/components/dialog/dialogContent";
import dialogContentText from "materialDashboard/theme/components/dialog/dialogContentText";
import dialogActions from "materialDashboard/theme/components/dialog/dialogActions";

export default createTheme({
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...container,
      },
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiNativeSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    //MuiCheckbox: { ...checkbox },  // the custom checkbox is not working with the indeterminate state
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions },
  },
});
